/* eslint-disable no-useless-escape */
import { FC } from 'react';
import {
  Button,
  Icon,
  Input,
  TextTitle,
  TextBody,
  TextOverline,
  Spacer,
  Stack,
  Grid,
  GridCell,
  Card,
  Tooltip,
  SelectOption,
  Select,
  AccordionItem,
  TextSubtitle,
  Callout,
  CalloutTitle,
} from '@pypestream/design-system';
import { TranslationComponent, useTranslation } from '@pypestream/translations';
import { FieldValues, SubmitHandler, useFormContext } from 'react-hook-form';

import { Credentials } from '../../../xstate/prostudio.api-service.types';
import { SupervisorKaInfo } from './supervisor-ka-info';
import { useSolutionNameValidation } from '../../../hooks';
import './step-initial.scss';

export const StepInitial: FC<{
  nextStep: SubmitHandler<FieldValues>;
  createSolution?: (data: {
    formData?: Partial<{ botName: string; type: 'supervisor' | 'blank' }>;
    callback: () => void;
  }) => void;
  isLoading?: boolean;
  credentials?: Credentials;
  onSuccess: () => void;
  disabled?: boolean;
}> = ({
  nextStep,
  createSolution,
  isLoading,
  credentials,
  onSuccess,
  disabled = false,
}) => {
  const [t] = useTranslation();

  const { handleSubmit, register, setValue, watch } = useFormContext();

  const watchedBotName = watch('botName');
  const watchedType = watch('type');

  const { hasLengthError, isValidName, getTag, validateSolutionName } =
    useSolutionNameValidation(watchedBotName);

  const handleTypeSelection = (type: 'supervisor' | 'blank') => {
    setValue('type', type);
  };

  const hasCredentials = credentials && credentials.length > 0;

  const isBlankType = watchedType === 'blank';

  const handleCreateButtonClick = () => {
    if (isBlankType) {
      createSolution?.({
        formData: {
          botName: watchedBotName,
          type: watchedType,
        },
        callback: onSuccess,
      });
    } else {
      handleSubmit(nextStep)();
    }
  };

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <TextTitle size="small" textAlign="center">
        <TranslationComponent i18nKey="pro-studio/homepage:modal.title">
          Create a new solution
        </TranslationComponent>
      </TextTitle>
      <Spacer size="small" />
      <TextBody variant="secondary" textAlign="center">
        <TranslationComponent i18nKey="pro-studio/homepage:vocabulary.create_modal_subtitle">
          Enter a Solution Title and choose how you want to start. You can begin
          with Supervisor and Knowledge agents or create a blank solution.
        </TranslationComponent>
      </TextBody>
      <Spacer size="xlarge" />
      <TextBody size="small">
        <TranslationComponent i18nKey="pro-studio/homepage:vocabulary.title_label">
          Solution Title*
        </TranslationComponent>
      </TextBody>
      <Spacer size="2xsmall" />
      <Input
        {...register('botName')}
        // @ts-expect-error - TODO: onInput event type has no value property
        onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
          setValue('botName', validateSolutionName(e.target.value));
        }}
        hasError={hasLengthError}
        helpText={
          hasLengthError
            ? (t('pro-studio/homepage:modal.form.name.validation.length', {
                defaultValue: 'Use 1–100 characters.',
              }) ?? undefined)
            : undefined
        }
        autofocus
        test-id="create-solution-modal-solution-name-input"
        variant="outlined"
        disabled={isLoading}
        placeholder={
          t('pro-studio/homepage:modal.form.name.placeholder', {
            defaultValue: 'Solution Title',
          }) || ''
        }
      />
      <Spacer size="small" />
      <Stack gutter="small">
        {getTag(
          t('pro-studio/homepage:modal.form.name.validation.uniqueness', {
            defaultValue: 'Uniqueness',
          }),
          'passedUniquenessCheck'
        )}
      </Stack>
      <Spacer size="large" />
      <Grid alignItems="stretch">
        <GridCell xsmall="6">
          <Card
            test-id="create-solution-modal-supervisor-card"
            size="xlarge"
            border={watchedType === 'supervisor' ? 'primary' : 'default'}
            interactive
            textAlign="center"
            onClick={() => handleTypeSelection('supervisor')}
          >
            <Stack
              direction="column"
              alignItems="center"
              justifyContent="center"
              gutter="xsmall"
              style={{ height: '100%' }}
            >
              <Icon name="elements-routing" size="xlarge" />
              <TextOverline textAlign="center">
                <TranslationComponent i18nKey="pro-studio/homepage:vocabulary.start_supervisor_ka">
                  Solution with Supervisor Agent & Knowledge Agent
                </TranslationComponent>
              </TextOverline>
            </Stack>
          </Card>
        </GridCell>
        <GridCell xsmall="6">
          <Card
            size="xlarge"
            border={isBlankType ? 'primary' : 'default'}
            interactive
            textAlign="center"
            onClick={() => handleTypeSelection('blank')}
          >
            <Stack
              direction="column"
              alignItems="center"
              justifyContent="center"
              gutter="xsmall"
              style={{ height: '100%' }}
            >
              <Icon name="elements-form" size="xlarge" />
              <TextOverline textAlign="center">
                <TranslationComponent i18nKey="pro-studio/homepage:vocabulary.start_blank">
                  Blank Solution
                </TranslationComponent>
              </TextOverline>
            </Stack>
          </Card>
        </GridCell>
      </Grid>
      {!hasCredentials && watchedType === 'supervisor' && (
        <>
          <Spacer size="large" />
          <Callout size="small" theme="light">
            <Stack alignItems="center" gutter="xsmall">
              <Icon name="info" size="small" weight="medium" />
              <CalloutTitle>
                To use this option, you must first connect an AI Provider. Set
                this up in next step.
              </CalloutTitle>
            </Stack>
          </Callout>
        </>
      )}
      {watchedType === 'supervisor' && hasCredentials && (
        <>
          <Spacer size="large" />
          <Grid gutter="large" rowGutter="large">
            <GridCell xsmall="6">
              <Stack alignItems="center" gutter="2xsmall">
                <TextBody size="small">AI Provider</TextBody>
                <Tooltip
                  placement="right"
                  content="Choose the large language model (LLM) to power your supervisor, such as ChatGPT, Gemini, or Claude."
                >
                  <Icon name="info" color="gray" size="xsmall" />
                </Tooltip>
              </Stack>
              <Spacer size="2xsmall" />
              <Select
                test-id="create-solution-modal-model-select"
                variant="outlined"
                placeholder="Select a model"
                {...register('llmCredentialId')}
                onChange={(e) => {
                  const { value } = e.target as { value: string };
                  setValue('llmCredentialId', value);
                }}
              >
                {credentials?.map((credential) => (
                  <SelectOption key={credential.id} value={credential.id || ''}>
                    <Icon
                      name={
                        credential.llmProvider?.name?.toLowerCase() === 'openai'
                          ? 'gpt'
                          : credential.llmProvider?.name?.toLowerCase() ===
                              'anthropic'
                            ? 'claude'
                            : credential.llmProvider?.name?.toLowerCase() ===
                                'google'
                              ? 'gemini'
                              : 'gemini'
                      }
                      size="small"
                      slot="prefix"
                    />
                    {`${credential.llmProvider?.name || 'Gemini'}`}
                  </SelectOption>
                ))}
              </Select>
            </GridCell>
            <GridCell xsmall="6">
              <Stack alignItems="center" gutter="2xsmall">
                <TextBody size="small">Model Type</TextBody>
                <Tooltip
                  placement="right"
                  content="Choose the large language model (LLM) to power your supervisor, such as ChatGPT, Gemini, or Claude."
                >
                  <Icon name="info" color="gray" size="xsmall" />
                </Tooltip>
              </Stack>
              <Spacer size="2xsmall" />
              <Select
                test-id="create-solution-modal-model-type-select"
                variant="outlined"
                placeholder="Select a model type"
              >
                <SelectOption value="gemini-2.5-flash">
                  gemini-2.5-flash
                </SelectOption>
              </Select>
            </GridCell>
          </Grid>
        </>
      )}
      <Spacer size="large" />
      <AccordionItem className="c-create-solution-initial__accordion">
        <TextSubtitle size="small" variant="secondary" slot="header">
          What are Supervisor & Knowledge Agents?
        </TextSubtitle>
        <div className="c-create-solution-initial__supervisor-ka">
          <SupervisorKaInfo />
        </div>
        <Spacer size="large" />
        <div className="c-create-solution-initial__info">
          <TextSubtitle size="small">
            To set up your agents, you will:
          </TextSubtitle>
          <Spacer size="xsmall" />
          <TextBody
            size="small"
            className="c-create-solution-initial__info-steps"
          >
            <ol className="u-margin-bottom-xsmall">
              <li>
                Name your Supervisor Agent & give it context of your brand.
              </li>
              <li>
                <TranslationComponent i18nKey="pro-studio/homepage:vocabulary.tasks_hint">
                  List the tasks you want your solution to be capable of. Each
                  task becomes the starting point of a new workflow.
                </TranslationComponent>
              </li>
              <li>
                Upload documents to provide the Knowledge Agent with the context
                it needs.
              </li>
              <li>
                Give your Knowledge Agent a tone & personality that aligns with
                your brand.
              </li>
            </ol>
          </TextBody>
          <TextBody size="small">
            This setup creates a pair of intelligent assistants that can handle
            conversations, route users to the right workflows, and provide
            accurate, brand-specific answers.
          </TextBody>
        </div>
      </AccordionItem>
      <Spacer size="xlarge" />
      <Stack justifyContent="end">
        <Button
          test-id="create-solution-modal-create-btn"
          size="large"
          type="button"
          disabled={isLoading || !isValidName || disabled}
          onClick={handleCreateButtonClick}
        >
          {isBlankType ? (
            <TranslationComponent
              i18nKey={
                isLoading
                  ? 'pro-studio/homepage:modal.buttons.creating'
                  : 'pro-studio/homepage:modal.buttons.create'
              }
            >
              {isLoading ? 'Creating...' : 'Create'}
            </TranslationComponent>
          ) : (
            'Next'
          )}
        </Button>
      </Stack>
    </form>
  );
};
